<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 animate-modalFade">
    <div class="w-full max-w-2xl bg-gray-800 text-indigo-200 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-600 animate-scaleIn">
      
      <!-- Countdown Timer -->
      <div v-if="timeLeft !== null" class="text-center font-semibold text-xs sm:text-sm">
        <div class="text-center text-xs sm:text-sm">{{ mapName }}</div>
        <p :class="timeLeft <= 420000 ? 'text-red-500 ' : 'text-yellow-400'">
          เหลือเวลา: {{ formatTime(timeLeft) }}
        </p>
        <p v-if="!fightResult && timeLeft <= 420000" class="text-red-500 text-xs mt-1 ">
          <i class="fas fa-exclamation-triangle mr-1"></i> คำเตือน: หากการต่อสู้ไม่รู้ผลก่อนหมดเวลา ผู้เล่นจะตาย และต้องรีบกลับบ้านภายในเวลาที่กำหนดทันที !!!
        </p>
        <div class="text-xs sm:text-sm font-bold text-indigo-300 mt-2">
          <i class="fas fa-skull mr-1"></i> หุ่นยนต์ที่ฆ่า: {{ robotKilled }}
        </div>
      </div>

      <!-- Initial Loading -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center py-4">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        <p class="text-indigo-200 text-sm mt-2">กำลังโหลด...</p>
      </div>

      <!-- Robot Section -->
      <div v-if="robotData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 rounded-md p-4 border border-gray-700">
        <div class="flex items-center gap-3 w-full">
          <img
            :src="robotData?.img || 'default-robot.png'"
            alt="Robot"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-purple-500"
            @error="handleImageError"
          />
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-purple-500 text-center">{{ robotData?.name || 'หุ่นยนต์' }}  </h2>
            <div class="grid grid-cols-7 gap-2 text-center mt-2 bg-purple-900/20 p-2 rounded-md">
              <div class="flex flex-col items-center">
                <i class="fas fa-heartbeat text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-fist-raised text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-running text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-crosshairs text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-shield-virus text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-shield-alt text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-person-rays text-purple-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ robotStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full relative">
          <div class="bg-gray-600 h-5 rounded overflow-hidden">
            <div
              class="bg-purple-500 h-5 rounded transition-all duration-200"
              :style="{ width: '100%' }"
            ></div>
            <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
              HP: ???? / {{ robotStats.maxHp }}
            </p>
          </div>
        </div>
      </div>

      <!-- Player Section -->
      <div v-if="robotData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 rounded-md p-4 border border-gray-700">
        <div class="flex items-center gap-3 w-full">
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-blue-400 text-center">{{ characterData?.characterName || 'ผู้เล่น' }}</h2>
            <div class="grid grid-cols-7 gap-2 text-center mt-2 bg-blue-900/20 p-2 rounded-md">
              <div class="flex flex-col items-center">
                <i class="fas fa-heartbeat text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-fist-raised text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-running text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-crosshairs text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-shield-virus text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-shield-alt text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-person-rays text-blue-300 text-md sm:text-lg"></i>
                <p class="text-xs sm:text-sm font-bold text-indigo-300 mt-1">{{ calculatedStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
          <img
            :src="characterData?.imageUrl || 'default-character.png'"
            alt="Character"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-blue-500"
            @error="handleImageError"
          />
        </div>
        <div class="w-full space-y-2">
          <!-- HP Bar -->
          <div class="relative">
            <div class="bg-gray-600 h-5 rounded overflow-hidden">
              <div
                class="bg-red-500 h-5 rounded transition-all duration-200"
                :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"
              ></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>
          <!-- Infection Bar -->
          <div class="relative">
            <div class="bg-gray-600 h-5 rounded overflow-hidden">
              <div
                class="bg-green-500 h-5 rounded transition-all duration-200"
                :style="{ width: `${infectionLevel || 0}%` }"
              ></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
                ติดเชื้อ: {{ infectionLevel || 0 }}%
              </p>
            </div>
          </div>
          <!-- Energy Bar -->
          <div class="relative">
            <div class="bg-gray-600 h-5 rounded overflow-hidden">
              <div
                class="bg-blue-500 h-5 rounded transition-all duration-200"
                :style="{ width: `${(energyLevel / maxEnergy * 100) || 0}%` }"
              ></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
                พลังงาน: {{ energyLevel || 0 }} / {{ maxEnergy }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Player Section when no robot -->
      <div
        v-if="!robotData && !isLoading"
        class="flex flex-col items-center gap-3 bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-800"
      >
        <!-- Bars in One Line -->
        <div class="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <!-- HP Bar -->
          <div class="relative flex-1">
            <div class="bg-gray-600 h-4 sm:h-5 rounded overflow-hidden">
              <div
                class="bg-red-500 h-4 sm:h-5 rounded transition-all duration-200"
                :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"
              ></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>

          <!-- Infection Bar -->
          <div class="relative flex-1">
            <div class="bg-gray-600 h-4 sm:h-5 rounded overflow-hidden">
              <div
                class="bg-green-500 h-4 sm:h-5 rounded transition-all duration-200"
                :style="{ width: `${infectionLevel || 0}%` }"
              ></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
                ติดเชื้อ: {{ infectionLevel || 0 }}%
              </p>
            </div>
          </div>

          <!-- Energy Bar -->
          <div class="relative flex-1">
            <div class="bg-gray-600 h-4 sm:h-5 rounded overflow-hidden">
              <div
                class="bg-blue-500 h-4 sm:h-5 rounded transition-all duration-200"
                :style="{ width: `${(energyLevel / maxEnergy * 100) || 0}%` }"
              ></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-white">
                พลังงาน: {{ energyLevel || 0 }} / {{ maxEnergy }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Rewards Section when hasReward and no robot -->
      <div v-if="!robotData && hasReward && !isLoading" class="flex flex-col gap-3">
        <div class="bg-gray-600 p-2 rounded-md flex items-center justify-between gap-2 border border-gray-600">
          <div class="flex items-center gap-3 flex-wrap">
            <div class="flex items-center gap-2">
              <span class="text-xxs sm:text-xs text-gray-300">น้ำหนัก</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="px-2 py-0.5 rounded bg-gray-700 text-gray-100 text-xxs font-semibold border border-gray-500">
                {{ selectedWeight }} / {{ capacity }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button
              @click="toggleAllSelections"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-xxs sm:text-xs font-semibold focus:outline-none focus:ring-2"
              :class="selectedRewardIds.length
                ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-700'
                : 'bg-green-600 hover:bg-green-700 focus:ring-green-700'"
              :disabled="isFighting || (!rewards.length && !selectedRewardIds.length)"
              :title="selectedRewardIds.length ? 'ยกเลิกการเลือกทั้งหมด' : 'เลือกทั้งหมดแบบพอดีน้ำหนัก'">
              <i v-if="!selectedRewardIds.length" class="fas fa-check-double text-xs"></i>
              <i v-else class="fas fa-rotate-left text-xs"></i>
              <span class="hidden xs:inline">{{ selectedRewardIds.length ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด' }}</span>
              <span class="inline sm:hidden">{{ selectedRewardIds.length ? 'ยกเลิก' : 'เลือก' }}</span>
            </button>
          </div>
        </div>
        <ul class="bg-gray-600 rounded-md p-2 max-h-64 overflow-y-auto scrollbar-thin border border-gray-600">
          <li
            v-for="reward in rewards"
            :key="reward.id"
            @click="toggleRewardSelection(reward.id)"
            class="flex items-center gap-3 p-2 cursor-pointer transition-all duration-200 rounded-md mb-2 last:mb-0 border"
            :class="selectedRewardIds.includes(reward.id)
              ? 'bg-gray-700 border-l-4 border-indigo-500 '
              : 'bg-gray-700 bg-opacity-40 hover:bg-opacity-60 border-gray-600'">
            <img
              :src="reward.img || 'default-item.png'"
              alt="Reward"
              class="w-10 h-10 rounded-md object-cover border-2"
              :class="reward.rarityColors?.borderColor"
              @error="handleImageError"
            />
            <div class="flex-1 min-w-0">
              <p class="text-xs font-semibold truncate" :class="reward.rarityColors?.textColor || 'text-gray-100'">
                {{ reward.name }} <span v-if="reward.amount">x {{ reward.amount }}</span>
                <i v-if="reward.transfer" class="fas fa-arrow-right-arrow-left text-green-500 text-xxs ml-1"></i>
              </p>
              <p class="text-xxs text-gray-400">น้ำหนัก: {{ reward.weight || 0 }}</p>
            </div>
            <i class="fas" :class="selectedRewardIds.includes(reward.id) ? 'fa-check-circle text-green-500' : 'fa-circle text-gray-500'"></i>
          </li>
          <li v-if="!rewards.length" class="text-center text-gray-300 text-xs py-4">
            ไม่มีรางวัลให้เลือก
          </li>
        </ul>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col gap-2">
        <button
          v-if="robotData"
          @click="attackRobot"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || energyLevel <= 0"
        >
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-skull-crossbones text-xs group-hover:animate-pulse"></i>
          โจมตี
        </button>
        <button
          v-if="robotData"
          @click="fleeRobot"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting"
        >
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          หนี (โอกาสสำเร็จ {{ fleeChance }}%)
        </button>
        <button
          v-if="!robotData && hasReward"
          @click="addToBag"
          class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || selectedRewardIds.length === 0 || selectedWeight > capacity"
        >
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-shopping-bag text-xs group-hover:animate-pulse"></i>
          นำใส่กระเป๋า ({{ selectedRewardIds.length }} รายการ)
        </button>
        <button
          v-if="!robotData"
          @click="handleGoHomeClick"
          class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :class="isConfirmingGoHome ? 'bg-red-600 hover:bg-red-500' : 'bg-indigo-600 hover:bg-indigo-700'"
          :disabled="isFighting"
        >
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-home text-xs group-hover:animate-bounce"></i>
          {{ isConfirmingGoHome ? 'ยืนยันกลับบ้าน' : 'กลับบ้าน' }}
        </button>
      </div>

      <!-- Fight Result -->
      <p v-if="fightResult" class="text-xs text-indigo-300 text-center">{{ fightResult }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const gameActions = projectFunctions.httpsCallable('gameActions');
    const inventoryStore = useInventoryStore();
    const isLoading = ref(true);
    const robotData = ref(null);
    const characterData = computed(() => playerStore.characterData);
    const characterPrivateData = computed(() => playerStore.characterPrivateData);
    const currentRobotHp = ref(0);
    const fightResult = ref('');
    const isFighting = ref(false);
    const robotKilled = ref(0);
    const expiresAt = ref(null);
    const timeLeft = ref(null);
    const energyLevel = computed(() => playerStore.characterPrivateData?.energyLevel || 0);
    const maxEnergy = ref(4800);
    const isConfirmingGoHome = ref(false);
    const mapName = ref('');
    const ownerName = ref('');
    const hasReward = ref(false);
    const rewards = ref([]);
    const selectedRewardIds = ref([]);
    const currentWeight = ref(0);
    const maxLoad = ref(0);
    const eventData = ref({ diamondReward: 0, rewardItems: {}, rewardDataMap: {} });
    let timer = null;
    let confirmTimeout = null;
    let unsubscribeEvent = null;
    let unsubscribeRobot = null;

    const robotStats = computed(() => {
      if (!robotData.value) return { maxHp: 100, atk: 0, evasion: 0, critical: 0, defense: 0, criticalReduction: 0, damageReduction: 0, flee: 0 };
      const baseStats = robotData.value.stats || { stamina: 0, str: 0, int: 0, agi: 0, defense: 0, maxHp: 0, atk: 0, critical: 0, evasion: 0, luck: 0 };
      const itemStats = robotData.value.itemUpgradeStats || { stamina: 0, str: 0, int: 0, agi: 0, defense: 0, luck: 0 };
      const silverStats = robotData.value.silverUpgradeStats || { stamina: 0, str: 0, int: 0, agi: 0, defense: 0, luck: 0 };
      const totalStamina = (baseStats.stamina || 0) + (itemStats.stamina || 0) + (silverStats.stamina || 0);
      const totalStr = (baseStats.str || 0) + (itemStats.str || 0) + (silverStats.str || 0);
      const totalInt = (baseStats.int || 0) + (itemStats.int || 0) + (silverStats.int || 0);
      const totalAgi = (baseStats.agi || 0) + (itemStats.agi || 0) + (silverStats.agi || 0);
      const totalDefense = (baseStats.defense || 0) + (itemStats.defense || 0) + (silverStats.defense || 0);
      const totalLuck = (baseStats.luck || 0) + (itemStats.luck || 0) + (silverStats.luck || 0);
      const defenseBonus = Math.floor(totalDefense / 20);
      return {
        maxHp: totalStamina * 20 + (baseStats.maxHp || 0),
        atk: totalStr * 2 + (baseStats.atk || 0),
        critical: Math.floor(totalInt / 2) + (baseStats.critical || 0),
        evasion: Math.floor(totalAgi / 2) + (baseStats.evasion || 0),
        defense: totalDefense,
        criticalReduction: totalLuck + defenseBonus,
        damageReduction: defenseBonus,
        flee: Math.floor((Math.floor(totalAgi / 2) + Math.floor(totalInt / 2)) / 2),
      };
    });

    const calculatedStats = computed(() => {
      if (!characterPrivateData.value) return { maxHp: 100, atk: 0, critical: 0, evasion: 0, defense: 0, criticalReduction: 0, damageReduction: 0, flee: 0 };
      const stats = characterPrivateData.value;
      const str = 
        (stats.itemStats?.str || 0) + 
        (stats.bonusStats?.str || 0) +
        (stats.stats?.str || 0) + 
        (stats.traitStats?.str || 0) + 
        (stats.skillStats?.str || 0) + 
        (stats.titleStats?.str || 0) + 
        (stats.tattooStats?.str || 0) +
        (stats.ghostStats?.str || 0);
      const stamina = 
        (stats.itemStats?.stamina || 0) + 
        (stats.bonusStats?.stamina || 0) +
        (stats.stats?.stamina || 0) + 
        (stats.traitStats?.stamina || 0) + 
        (stats.skillStats?.stamina || 0) + 
        (stats.titleStats?.stamina || 0) + 
        (stats.tattooStats?.stamina || 0) +
        (stats.ghostStats?.stamina || 0);
      const int = 
        (stats.itemStats?.int || 0) + 
        (stats.bonusStats?.int || 0) +
        (stats.stats?.int || 0) + 
        (stats.traitStats?.int || 0) + 
        (stats.skillStats?.int || 0) + 
        (stats.titleStats?.int || 0) + 
        (stats.tattooStats?.int || 0) +
        (stats.ghostStats?.int || 0);
      const agi = 
        (stats.itemStats?.agi || 0) + 
        (stats.bonusStats?.agi || 0) +
        (stats.stats?.agi || 0) + 
        (stats.traitStats?.agi || 0) + 
        (stats.skillStats?.agi || 0) + 
        (stats.titleStats?.agi || 0) + 
        (stats.tattooStats?.agi || 0) +
        (stats.ghostStats?.agi || 0);
      const luck = 
        (stats.itemStats?.luck || 0) + 
        (stats.bonusStats?.luck || 0) +
        (stats.stats?.luck || 0) + 
        (stats.traitStats?.luck || 0) + 
        (stats.skillStats?.luck || 0) + 
        (stats.titleStats?.luck || 0) + 
        (stats.tattooStats?.luck || 0) +
        (stats.ghostStats?.luck || 0);
      const defense = 
        (stats.itemStats?.defense || 0) + 
        (stats.bonusStats?.defense || 0) +
        (stats.stats?.defense || 0) +
        (stats.traitStats?.defense || 0) +
        (stats.skillStats?.defense || 0) +
        (stats.titleStats?.defense || 0) +
        (stats.tattooStats?.defense || 0) +
        (stats.ghostStats?.defense || 0);
      const flatMaxHp =
        (stats.itemStats?.maxHp || 0) +
        (stats.bonusStats?.maxHp || 0) +
        (stats.stats?.maxHp || 0) +
        (stats.traitStats?.maxHp || 0) +
        (stats.skillStats?.maxHp || 0) +
        (stats.titleStats?.maxHp || 0) +
        (stats.tattooStats?.maxHp || 0) +
        (stats.ghostStats?.maxHp || 0);
      const flatAtk =
        (stats.itemStats?.atk || 0) +
        (stats.bonusStats?.atk || 0) +
        (stats.stats?.atk || 0) +
        (stats.traitStats?.atk || 0) +
        (stats.skillStats?.atk || 0) +
        (stats.titleStats?.atk || 0) +
        (stats.tattooStats?.atk || 0) +
        (stats.ghostStats?.atk || 0);
      const flatCritical =
        (stats.itemStats?.critical || 0) +
        (stats.bonusStats?.critical || 0) +
        (stats.stats?.critical || 0) +
        (stats.traitStats?.critical || 0) +
        (stats.skillStats?.critical || 0) +
        (stats.titleStats?.critical || 0) +
        (stats.tattooStats?.critical || 0) +
        (stats.ghostStats?.critical || 0);
      const flatEvasion =
        (stats.itemStats?.evasion || 0) +
        (stats.bonusStats?.evasion || 0) +
        (stats.stats?.evasion || 0) +
        (stats.traitStats?.evasion || 0) +
        (stats.skillStats?.evasion || 0) +
        (stats.titleStats?.evasion || 0) +
        (stats.tattooStats?.evasion || 0) +
        (stats.ghostStats?.evasion || 0);
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: stamina * 20 + flatMaxHp,
        atk: str * 2 + flatAtk,
        critical: Math.floor(int / 2) + flatCritical,
        evasion: Math.floor(agi / 2) + flatEvasion,
        defense: defense,
        criticalReduction: luck + defenseBonus,
        damageReduction: defenseBonus,
        flee: Math.floor((Math.floor(agi / 2) + Math.floor(int / 2)) / 2),
      };
    });

    const currentHealth = computed(() => {
      const damage = characterPrivateData.value?.damageTaken || 0;
      return Math.max(0, calculatedStats.value.maxHp - damage);
    });

    const infectionLevel = computed(() => {
      return characterPrivateData.value?.infectionLevel || 0;
    });

    const fleeChance = computed(() => {
      if (!robotData.value || !characterPrivateData.value) return 5;
      const playerFlee = calculatedStats.value.flee;
      const robotFlee = robotStats.value.flee;
      const fleeBase = Math.max(0, playerFlee - robotFlee);
      const fleeValue = Math.floor(fleeBase / 2);
      return Math.min(100, 10 + fleeValue);
    });

    const selectedWeight = computed(() => {
      return rewards.value
        .filter((reward) => selectedRewardIds.value.includes(reward.id))
        .reduce((sum, reward) => sum + (reward.weight || 0), 0);
    });

    const capacity = computed(() => Math.max(0, (maxLoad.value || 0) - (currentWeight.value || 0)));

    const rarityColors = (rarity) => {
      switch (rarity) {
        case 1: return { bgColor: 'bg-gray-500', textColor: 'text-gray-500', borderColor: 'border-gray-600' };
        case 2: return { bgColor: 'bg-stone-500', textColor: 'text-stone-500', borderColor: 'border-stone-600' };
        case 3: return { bgColor: 'bg-green-500', textColor: 'text-green-500', borderColor: 'border-green-600' };
        case 4: return { bgColor: 'bg-blue-500', textColor: 'text-blue-500', borderColor: 'border-blue-600' };
        case 5: return { bgColor: 'bg-red-500', textColor: 'text-red-500', borderColor: 'border-red-600' };
        case 6: return { bgColor: 'bg-purple-500', textColor: 'text-purple-500', borderColor: 'border-purple-600' };
        case 7: return { bgColor: 'bg-orange-500', textColor: 'text-orange-500', borderColor: 'border-orange-600' };
        case 8: return { bgColor: 'bg-pink-500', textColor: 'text-pink-500', borderColor: 'border-pink-600' };
        case 9: return { bgColor: 'bg-yellow-500', textColor: 'text-yellow-500', borderColor: 'border-yellow-600' };
        case 10: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        default: return { bgColor: 'bg-gray-700', textColor: 'text-gray-500', borderColor: 'border-gray-600' };
      }
    };

    const getItemTemplateByName = (itemName) => {
      if (!itemName) return null;
      return gameItemsData[itemName] || null;
    };

    const toRewardPreview = (id, rewardData) => {
      if (!rewardData) return null;
      const rewardStates = rewardData.states || {};
      const templateId = rewardData.templateId || rewardData.itemName || rewardData.name;
      const itemTemplate = getItemTemplateByName(templateId) || {};
      const rarity = rewardStates.rarity || rewardData.rarity || itemTemplate.rarity || 1;
      return {
        id,
        ...rewardData,
        ...rewardStates,
        name: rewardData.name || rewardData.itemName || itemTemplate.name || templateId || 'ไม่ทราบชื่อ',
        img: rewardData.img || rewardData.imageUrl || itemTemplate.img || itemTemplate.imageUrl || 'default-item.png',
        weight: rewardData.weight ?? itemTemplate.weight ?? 0,
        rarity,
        rarityColors: rewardStates.rarityColors || rewardData.rarityColors || itemTemplate.rarityColors || rarityColors(rarity),
        transfer: rewardStates.transfer ?? rewardData.transfer ?? false,
      };
    };

    const formatTime = (ms) => {
      if (ms <= 0) return 'หมดเวลา!';
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / (1000 * 60)) % 60);
      return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
    };

    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };

    const startCountdown = () => {
      if (expiresAt.value && !timer) {
        timeLeft.value = expiresAt.value - Date.now();
        if (timeLeft.value > 0) {
          timer = setInterval(() => {
            const now = Date.now();
            timeLeft.value = expiresAt.value - now;
            if (timeLeft.value <= 0) {
              clearInterval(timer);
              timer = null;
              if (!fightResult.value) {
                fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
                emit('fightCompleted');
              }
            }
          }, 1000);
        } else {
          timeLeft.value = 0;
          if (!fightResult.value) {
            fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
            emit('fightCompleted');
          }
        }
      }
    };

    const stopCountdown = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const fetchRewards = async (rewardItemIds, eventSnapshot = eventData.value) => {
      rewards.value = [];
      const embeddedRewardItems = eventSnapshot?.rewardItems || {};
      const embeddedRewardMap = eventSnapshot?.rewardDataMap || {};
      if (Number(eventSnapshot?.diamondReward || 0) > 0) {
        rewards.value.push({
          id: 'diamond',
          name: 'เพชร',
          img: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%9E%E0%B8%8A%E0%B8%A3.png?alt=media&token=d0f95851-b518-46cd-aa78-4729cc6d9f41',
          amount: Number(eventSnapshot?.diamondReward || 0),
          weight: 0,
          rarity: 10,
          rarityColors: rarityColors(10),
          transfer: false,
        });
      }
      for (const itemId of rewardItemIds || []) {
        const embeddedReward = toRewardPreview(itemId, embeddedRewardItems[itemId] || embeddedRewardMap[itemId]);
        if (embeddedReward) {
          rewards.value.push(embeddedReward);
        }
      }
      selectedRewardIds.value = [];
    };

    const subscribeToData = async () => {
      try {
        unsubscribeEvent = projectFirestore.collection('events').doc(props.eventId)
          .onSnapshot(async (doc) => {
            if (doc.exists) {
              const data = doc.data();
              eventData.value = {
                ...data,
                diamondReward: data.diamondReward || 0,
              };
              robotKilled.value = data.robotKilled || 0;
              mapName.value = data.mapName || '';
              hasReward.value = data.hasReward || false;

              const newExpiresAt = data.expiresAt?.toDate().getTime() || null;

              if (newExpiresAt !== expiresAt.value) {
                expiresAt.value = newExpiresAt;
                stopCountdown();
                if (expiresAt.value) {
                  startCountdown();
                }
              } else if (expiresAt.value && !timer) {
                startCountdown();
              }

              if (data.currentRobotId) {
                if (!unsubscribeRobot || robotData.value?.id !== data.currentRobotId) {
                  if (unsubscribeRobot) unsubscribeRobot();
                  unsubscribeRobot = projectFirestore.collection('killerRobots').doc(data.currentRobotId)
                    .onSnapshot(async (robotDoc) => {
                      if (robotDoc.exists) {
                        robotData.value = { id: robotDoc.id, ...robotDoc.data() };
                        currentRobotHp.value = robotStats.value.maxHp - (robotData.value.damageTaken || 0);
                        // Fetch owner name
                        const ownerUid = robotData.value.ownerUid;
                        if (ownerUid) {
                          const ownerDoc = await projectFirestore.collection('characters').doc(ownerUid).get();
                          if (ownerDoc.exists) {
                            ownerName.value = ownerDoc.data().characterName || 'ไม่ทราบชื่อ';
                          } else {
                            ownerName.value = 'ไม่ทราบชื่อ';
                          }
                        } else {
                          ownerName.value = 'ไม่ทราบชื่อ';
                        }
                      } else {
                        robotData.value = null;
                        currentRobotHp.value = 0;
                        ownerName.value = '';
                      }
                    });
                }
              } else {
                if (unsubscribeRobot) {
                  unsubscribeRobot();
                  unsubscribeRobot = null;
                }
                robotData.value = null;
                currentRobotHp.value = 0;
                ownerName.value = '';
                if (hasReward.value && ((data.rewardItemIds?.length || 0) > 0 || Number(data.diamondReward || 0) > 0)) {
                  await fetchRewards(data.rewardItemIds, data);
                } else {
                  rewards.value = [];
                }
              }
            }
            isLoading.value = false;
          });

        watch(() => inventoryStore.bagData, (bagData) => {
          if (bagData) {
            currentWeight.value = bagData.currentWeight || 0;
            maxLoad.value = bagData.maxLoad || 100;
          }
        }, { immediate: true });
      } catch (err) {
        fightResult.value = 'ไม่สามารถโหลดข้อมูลได้';
        isLoading.value = false;
      }
    };

    const attackRobot = async () => {
      if (isFighting.value || energyLevel.value <= 0) return;
      isFighting.value = true;
      fightResult.value = 'กำลังโจมตี...';

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({ action: 'robotActions', subAction: 'attack', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (fightResult.value.includes('ผู้เล่นตาย')) {
          stopCountdown();
          emit('fightCompleted');
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการโจมตี';
      } finally {
        isFighting.value = false;
      }
    };

    const fleeRobot = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังหนี...';

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({ action: 'robotActions', subAction: 'flee', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data.completed) {
          stopCountdown();
          emit('fightCompleted');
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการหนี';
      } finally {
        isFighting.value = false;
      }
    };

    const handleGoHomeClick = () => {
      if (isFighting.value) return;

      if (isConfirmingGoHome.value) {
        goHome();
        isConfirmingGoHome.value = false;
        if (confirmTimeout) {
          clearTimeout(confirmTimeout);
          confirmTimeout = null;
        }
      } else {
        isConfirmingGoHome.value = true;
        fightResult.value = 'คลิกปุ่มอีกครั้งเพื่อยืนยันการกลับบ้าน';
        confirmTimeout = setTimeout(() => {
          isConfirmingGoHome.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };

    const goHome = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังกลับบ้าน...';

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({ action: 'robotActions', subAction: 'goHome', eventId: props.eventId });
        fightResult.value = response.data.message;
        stopCountdown();
        emit('fightCompleted');
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการกลับบ้าน';
      } finally {
        isFighting.value = false;
      }
    };

    const toggleRewardSelection = (rewardId) => {
      const reward = rewards.value.find((r) => r.id === rewardId);
      const newWeight = selectedRewardIds.value.includes(rewardId)
        ? selectedWeight.value
        : selectedWeight.value + (reward.weight || 0);
      if (newWeight > capacity.value) {
        fightResult.value = 'น้ำหนักเกิน! ไม่สามารถเลือกไอเทมเพิ่มได้';
        return;
      }
      if (selectedRewardIds.value.includes(rewardId)) {
        selectedRewardIds.value = selectedRewardIds.value.filter((id) => id !== rewardId);
      } else {
        selectedRewardIds.value = [...selectedRewardIds.value, rewardId];
      }
      fightResult.value = '';
    };

    const selectAllRewards = () => {
      if (!rewards.value.length) {
        fightResult.value = 'ไม่มีไอเทมให้เลือก!';
        return;
      }
      const availableWeight = capacity.value;
      let totalWeight = 0;
      const selectableRewards = rewards.value.filter((reward) => {
        totalWeight += reward.weight || 0;
        return totalWeight <= availableWeight;
      });
      if (selectableRewards.length === 0) {
        fightResult.value = 'น้ำหนักเกิน! ไม่สามารถเลือกไอเทมทั้งหมดได้';
        return;
      }
      selectedRewardIds.value = selectableRewards.map((reward) => reward.id);
      fightResult.value = `เลือก ${selectableRewards.length} ไอเทมเรียบร้อย`;
    };

    const toggleAllSelections = () => {
      if (selectedRewardIds.value.length) {
        selectedRewardIds.value = [];
        fightResult.value = 'ยกเลิกการเลือกทั้งหมดแล้ว';
      } else {
        selectAllRewards();
      }
    };

    const addToBag = async () => {
      if (isFighting.value || selectedRewardIds.value.length === 0) return;
      isFighting.value = true;
      fightResult.value = 'กำลังนำใส่กระเป๋า...';
      try {
        const response = await gameActions({
          action: 'addToBag100',
          eventId: props.eventId,
          rewardItemIds: selectedRewardIds.value,
        });
        fightResult.value = response.data.message;
        selectedRewardIds.value = [];
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการนำใส่กระเป๋า';
      } finally {
        isFighting.value = false;
      }
    };

    onMounted(() => {
      subscribeToData();
    });

    onUnmounted(() => {
      stopCountdown();
      if (confirmTimeout) clearTimeout(confirmTimeout);
      if (unsubscribeEvent) unsubscribeEvent();
      if (unsubscribeRobot) unsubscribeRobot();
    });

    return {
      mapName,
      isLoading,
      robotKilled,
      robotData,
      robotStats,
      currentRobotHp,
      characterData,
      characterPrivateData,
      calculatedStats,
      currentHealth,
      infectionLevel,
      energyLevel,
      maxEnergy,
      fightResult,
      isFighting,
      timeLeft,
      fleeChance,
      formatTime,
      handleImageError,
      attackRobot,
      fleeRobot,
      handleGoHomeClick,
      goHome,
      isConfirmingGoHome,
      ownerName,
      hasReward,
      rewards,
      selectedRewardIds,
      selectedWeight,
      capacity,
      toggleRewardSelection,
      toggleAllSelections,
      addToBag,
    };
  },
};

</script>

<style scoped>
/* Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}
.animate-pulse {
  animation: pulse 1.5s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.animate-bounce {
  animation: bounce 0.5s infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

/* Button States */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 640px) {
  .text-sm {
    font-size: 0.75rem;
  }
  .text-xs {
    font-size: 0.625rem;
  }
  .p-4 {
    padding: 0.75rem;
  }
  .w-16 {
    width: 3rem;
    height: 3rem;
  }
  .max-h-64 {
    max-height: 16rem;
  }
}
.text-xxs { font-size: 10px; line-height: 1rem; }
</style>
